// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { Rectangle } from "./Geometry.js";

CanvasRenderingContext2D.prototype.DrawLine = function (pen, X1, Y1, X2, Y2)
{
    this.beginPath();
    pen.applySettings(this);

    var crispCoef = this.lineWidth % 2 / 2;
    if (crispCoef) this.translate(crispCoef, crispCoef);

    this.moveTo(Math.round(X1), Math.round(Y1));
    this.lineTo(Math.round(X2), Math.round(Y2));
    this.stroke();

    if (crispCoef) this.translate(-crispCoef, -crispCoef);
};

CanvasRenderingContext2D.prototype.DrawLines = function (pen, points)
{
    if (!points) return;

    var len = points.length;
    if (len < 2) return;

    this.beginPath();
    pen.applySettings(this);

    var crispCoef = this.lineWidth % 2 / 2;
    if (crispCoef) this.translate(crispCoef, crispCoef);

    var p0 = points[0];
    this.moveTo(Math.round(p0.X), Math.round(p0.Y));

    for (var i = 1; i < len; i++)
    {
        var p = points[i];
        this.lineTo(Math.round(p.X), Math.round(p.Y));
    }

    this.stroke();

    if (crispCoef) this.translate(-crispCoef, -crispCoef);
};

CanvasRenderingContext2D.prototype.DrawRect = function (pen, X, Y, Width, Height)
{
    pen.applySettings(this);

    var crispCoef = this.lineWidth % 2 / 2;
    if (crispCoef) this.translate(crispCoef, crispCoef);

    this.strokeRect(
        Math.round(X),
        Math.round(Y),
        Math.round(Width),
        Math.round(Height));

    if (crispCoef) this.translate(-crispCoef, -crispCoef);
};

CanvasRenderingContext2D.prototype.FillRect = function (fillStyle, X, Y, Width, Height)
{
    fillStyle.applySettings(this);
    this.fillRect(
        Math.round(X),
        Math.round(Y),
        Math.round(Width),
        Math.round(Height));
};

CanvasRenderingContext2D.prototype.DrawPolygon = function (pen, points)
{
    if (!points) return;

    var len = points.length;
    if (!len || len < 3) return;


    this.beginPath();
    pen.applySettings(this);

    var pStart = points[0];
    this.moveTo(pStart.X, pStart.Y);

    for (var i = 1; i < len; i++)
    {
        var p = points[i];
        this.lineTo(p.X, p.Y);
    }

    this.closePath();
    this.stroke();
};

CanvasRenderingContext2D.prototype.FillPolygon = function (fillStyle, points)
{
    if (!points) return;

    var len = points.length;
    if (!len || len < 3) return;


    this.beginPath();
    fillStyle.applySettings(this);

    var pStart = points[0];
    this.moveTo(pStart.X, pStart.Y);

    for (var i = 1; i < len; i++)
    {
        var p = points[i];
        this.lineTo(p.X, p.Y);
    }

    this.closePath();
    this.fill();
};

CanvasRenderingContext2D.prototype.DrawString = function (text, font, brush, X, Y, textAlign, textBaseline)
{
    this.textAlign = textAlign || 'left';
    this.textBaseline = textBaseline || 'top';

    font.applySettings(this);
    brush.applySettings(this);

    this.fillText(text, Math.floor(X), Math.floor(Y));
};

CanvasRenderingContext2D.prototype.DrawEllipse = function (pen, x, y, w, h)
{

    var kappa = .5522848,
        ox = (w / 2) * kappa, // control point offset horizontal
        oy = (h / 2) * kappa, // control point offset vertical
        xe = x + w,           // x-end
        ye = y + h,           // y-end
        xm = x + w / 2,       // x-middle
        ym = y + h / 2;       // y-middle

    this.beginPath();
    pen.applySettings(this);
    this.moveTo(x, ym);
    this.bezierCurveTo(x, ym - oy, xm - ox, y, xm, y);
    this.bezierCurveTo(xm + ox, y, xe, ym - oy, xe, ym);
    this.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye);
    this.bezierCurveTo(xm - ox, ye, x, ym + oy, x, ym);
    //this.closePath(); // not used correctly, see comments (use to close off open path)
    this.stroke();
};

CanvasRenderingContext2D.prototype.FillEllipse = function (brush, x, y, w, h)
{

    var kappa = .5522848,
        ox = (w / 2) * kappa, // control point offset horizontal
        oy = (h / 2) * kappa, // control point offset vertical
        xe = x + w,           // x-end
        ye = y + h,           // y-end
        xm = x + w / 2,       // x-middle
        ym = y + h / 2;       // y-middle

    this.beginPath();
    brush.applySettings(this);
    this.moveTo(x, ym);
    this.bezierCurveTo(x, ym - oy, xm - ox, y, xm, y);
    this.bezierCurveTo(xm + ox, y, xe, ym - oy, xe, ym);
    this.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye);
    this.bezierCurveTo(xm - ox, ye, x, ym + oy, x, ym);
    //this.closePath(); // not used correctly, see comments (use to close off open path)
    this.fill();
    this.stroke();
};

CanvasRenderingContext2D.prototype.DrawArc = function (pen, centerX, centerY, radius, startAngleRad, endAngleRad)
{
    this.beginPath();
    pen.applySettings(this);

    var crispCoef = this.lineWidth % 2 / 2;
    if (crispCoef) this.translate(crispCoef, crispCoef);

    this.arc(Math.round(centerX), Math.round(centerY), Math.round(radius), startAngleRad, endAngleRad);
    this.stroke();

    if (crispCoef) this.translate(-crispCoef, -crispCoef);
};

CanvasRenderingContext2D.prototype.DrawCurve = function (pen, points)
{
    if (!points) return;

    var len = points.length;
    if (!len || len < 3) return;

    this.beginPath();
    pen.applySettings(this);

    var convPoints = [];
    for (var i = 0; i < len; i++)
    {
        var point = points[i];
        convPoints[i * 2] = point.X;
        convPoints[i * 2 + 1] = point.Y;
    }

    this.curve(convPoints);
    this.stroke();
};

CanvasRenderingContext2D.prototype.DrawFilledCurve = function (fillStyle, points)
{
    if (!points) return;

    var len = points.length;
    if (!len || len < 3) return;

    this.beginPath();
    fillStyle.applySettings(this);

    var convPoints = [];
    for (var i = 0; i < len - 2; i++)
    {
        var point = points[i];
        convPoints[i * 2] = point.X;
        convPoints[i * 2 + 1] = point.Y;
    }
    var pStart = points[len - 2];
    this.moveTo(pStart.X, pStart.Y);
    var pStart = points[len - 1];
    this.lineTo(pStart.X, pStart.Y);
    this.curve(convPoints);

    this.fill();
};

CanvasRenderingContext2D.prototype.DrawClosedCurve = function (pen, points)
{
    if (!points) return;

    var len = points.length;
    if (!len || len < 3) return;

    this.beginPath();
    pen.applySettings(this);

    var convPoints = [];
    for (var i = 0; i < len; i++)
    {
        var point = points[i];
        convPoints[i * 2] = point.X;
        convPoints[i * 2 + 1] = point.Y;
    }

    this.curve(convPoints, 0.5, 25, true);
    this.stroke();
};

CanvasRenderingContext2D.prototype.FillClosedCurve = function (fillStyle, points)
{
    if (!points) return;

    var len = points.length;
    if (!len || len < 3) return;

    this.beginPath();
    fillStyle.applySettings(this);

    var convPoints = [];
    for (var i = 0; i < len; i++)
    {
        var point = points[i];
        convPoints[i * 2] = point.X;
        convPoints[i * 2 + 1] = point.Y;
    }

    this.curve(convPoints, 0.5, 25, true);
    this.fill();
};

export const TO_RADIANS = Math.PI / 180;

CanvasRenderingContext2D.prototype.RotateByAngle = function (angle)
{
    this.rotate(angle * TO_RADIANS);
};

CanvasRenderingContext2D.prototype.DrawRotatedImage = function (image, x, y, angle)
{
    if (!image) return;

    this.save();

    this.translate(x, y);
    this.rotate(angle * TO_RADIANS);

    this.drawImage(image, -(image.width / 2), -(image.height / 2));

    this.restore();
}

CanvasRenderingContext2D.prototype.GetTextWidth = function (text, font)
{
    this.save();
    font.applySettings(this);
    var w = this.measureText(text).width;
    this.restore();
    return w;
};

CanvasRenderingContext2D.prototype.ToImage = function ()
{
    var image = new Image();
    image.src = this.canvas.toDataURL("image/png");
    return image;
};

CanvasRenderingContext2D.prototype.Clear = function ()
{
    this.clearRect(0, 0, this.width, this.height);
};

CanvasRenderingContext2D.prototype.RenderButton = function (brush, pen, rect)
{
    var cpy_rect = rect.copy();
    cpy_rect.Inflate(-2, -2);
    this.RoundRectangle(cpy_rect, 2, brush, pen, false, RectangleEdgeFilter.All);
};

CanvasRenderingContext2D.prototype.getPixelColor = function (x, y)
{
    var data = this.getImageData(x, y, 1, 1).data;
    return Color.CreateColor(data[0], data[1], data[2], data[3] / 255);
};

CanvasRenderingContext2D.prototype.DrawPolyLine = function (pen, polyLine)
{
    if (polyLine.lines.length === 0)
        return;

    this.beginPath();
    pen.applySettings(this);

    var crispCoef = this.lineWidth % 2 / 2;
    if (crispCoef) this.translate(crispCoef, crispCoef);

    var lines = polyLine.lines;
    var i;
    var len = lines.length;
    for (i = 0; i < len; i++)
    {
        var curLine = lines[i];
        this.moveTo(Math.round(curLine.X1), Math.round(curLine.Y1));
        this.lineTo(Math.round(curLine.X2), Math.round(curLine.Y2));
    }


    this.stroke();

    if (crispCoef) this.translate(-crispCoef, -crispCoef);
};

CanvasRenderingContext2D.prototype.DrawPolyRect = function (fillStyle, polyRect, penStyle)
{
    if (polyRect.rects.length === 0)
        return;

    fillStyle.applySettings(this);



    var rects = polyRect.rects;
    var i;
    var len = rects.length;
    for (i = 0; i < len; i++)
    {
        var curRect = rects[i];
        this.fillRect(
            Math.round(curRect.X),
            Math.round(curRect.Y),
            Math.round(curRect.Width),
            Math.round(curRect.Height));
        //if (usePen)
        //    this.strokeRect(
        //        Math.round(curRect.X),
        //        Math.round(curRect.Y),
        //        Math.round(curRect.Width-1),
        //        Math.round(curRect.Height));
    }

    var usePen = penStyle !== undefined;
    if (usePen)
    {
        penStyle.applySettings(this);
        var crispCoef = this.lineWidth % 2 / 2;
        if (crispCoef) this.translate(crispCoef, crispCoef);
    }

    for (i = 0; i < len; i++)
    {
        var curRect = rects[i];
        //this.fillRect(
        //    Math.round(curRect.X),
        //    Math.round(curRect.Y),
        //    Math.round(curRect.Width),
        //    Math.round(curRect.Height));
        if (usePen)
            this.strokeRect(
                Math.round(curRect.X),
                Math.round(curRect.Y),
                Math.round(curRect.Width - 1),
                Math.round(curRect.Height));
    }
    if (usePen)
        if (crispCoef) this.translate(-crispCoef, -crispCoef);
};

CanvasRenderingContext2D.prototype.DrawPolyText = function (polyText, font, brush, textAlign, textBaseline)
{
    if (polyText.textArray.length === 0)
        return;

    this.textAlign = textAlign || 'left';
    this.textBaseline = textBaseline || 'top';

    font.applySettings(this);
    brush.applySettings(this);

    var textItems = polyText.textArray;
    var i;
    var len = textItems.length;
    for (i = 0; i < len; i++)
    {
        var curTextItem = textItems[i];
        this.fillText(curTextItem.Text, Math.floor(curTextItem.X), Math.floor(curTextItem.Y));
    }
};

//#region RoundRectangle Methods.

CanvasRenderingContext2D.prototype.RoundRectangle = function (rect, rad, br, borderPen, showOutLine, filter)
{
    var RW = rect.Width;
    var RH = rect.Height;

    if (!RW || !RH)
        return;

    var RX = rect.X;
    var RY = rect.Y;
    var RR = RX + RW;
    var RB = RY + RH;

    var gr = this;
    gr.beginPath();

    if (br)
        br.applySettings(gr);
    if (borderPen)
        borderPen.applySettings(gr);

    var crispCoef = gr.lineWidth % 2 / 2;
    if (crispCoef) gr.translate(crispCoef, crispCoef);

    var diameter = rad * 2;
    var arc = new Rectangle(RX, RY, diameter, diameter);

    var PI = Math.PI;
    var halfPI = 0.5 * PI;
    var oneAndHalfPI = 1.5 * PI;

    var leftArcX = Math.round(RX + rad);
    var topArcY = Math.round(RY + rad);
    var rightArcX = Math.round(RR - rad);
    var bottomArcY = Math.round(RB - rad);

    if (RectangleEdgeFilter.IsTopLeft(filter))
    {
        gr.arc(leftArcX, topArcY, rad, PI, oneAndHalfPI);
    }
    else
    {
        var arcX = arc.X;
        var arcY = arc.Y;

        // gr.moveTo(arcX, arcY + arc.Height);
        gr.lineTo(arcX, arcY);
        gr.lineTo(arcX + arc.Width, arcY);
    }

    arc.X = RR - diameter;

    if (RectangleEdgeFilter.IsTopRight(filter))
    {
        gr.arc(rightArcX, topArcY, rad, oneAndHalfPI, 0);
    }
    else
    {
        var arcX = arc.X;
        var arcY = arc.Y;
        var arcR = arcX + arc.Width;

        // gr.moveTo(arcX, arcY);
        gr.lineTo(arcR, arcY);
        gr.lineTo(arcR, arcY + arc.Height);
    }

    arc.Y = RB - diameter;

    if (RectangleEdgeFilter.IsBottomRight(filter))
    {
        gr.arc(rightArcX, bottomArcY, rad, 0, halfPI);
    }
    else
    {
        var arcX = arc.X;
        var arcY = arc.Y;
        var arcR = arcX + arc.Width;
        var arcB = arcY + arc.Height;

        //gr.moveTo(arcR, arcY);
        gr.lineTo(arcR, arcB);
        gr.lineTo(arcX, arcB);
    }

    arc.X = RX;

    if (RectangleEdgeFilter.IsBottomLeft(filter))
    {
        gr.arc(leftArcX, bottomArcY, rad, halfPI, PI);
    }
    else
    {
        var arcX = arc.X;
        var arcY = arc.Y;
        var arcB = arcY + arc.Height;

        //gr.moveTo(arcX + arc.Width, arcB);
        gr.lineTo(arcX, arcB);
        gr.lineTo(arcX, arcY);
    }

    gr.closePath();
    gr.fill();
    gr.stroke();

    if (crispCoef) gr.translate(-crispCoef, -crispCoef);

    //
    // Наводка белым цветом
    //
    //TODO REMOVE not USE
    // if (showOutLine)
    // {
    //     var wPen = Pens.White;

    //     gr.DrawLine(wPen, RX + rad / 2, RB - 1, RR - rad / 2, RB - 1);
    //     gr.DrawLine(wPen, RR - 1, RY + rad / 2, RR - 1, RB - rad / 2);
    //     gr.DrawArc(wPen, RR - rad - 1, RB - rad - 1, rad, 0, endingAngleRad);
    // }
};

CanvasRenderingContext2D.prototype.RoundRectangleGradient = function (rect, rad, /*text,*/ col1, col2, /*textColor,*/ borderPen, showOutLine, /*sf, f,*/ leftScip, rightScip)
{
    var rectX = rect.X;

    var br = new LinearGradientBrush(
        rectX, 0,
        rectX + rect.Width, 0,
        col1, col2);

    this.RoundRectangleFilter(rect, rad, /*text,*/ br, /*textColor,*/ borderPen, showOutLine, /*sf, f,*/ leftScip, rightScip);
};

CanvasRenderingContext2D.prototype.RoundRectangleFilter = function (rect, rad, /*text,*/ br, /*textColor,*/ borderPen, showOutLine, /*sf, f,*/ leftScip, rightScip)
{

    var filter = RectangleEdgeFilter.All;
    if (leftScip) filter = RectangleEdgeFilter.All & (RectangleEdgeFilter.BottomRight | RectangleEdgeFilter.TopRight);
    if (rightScip) filter = RectangleEdgeFilter.All & (RectangleEdgeFilter.BottomLeft | RectangleEdgeFilter.TopLeft);

    this.RoundRectangle(rect, rad, /*text,*/ br, /*textColor,*/ borderPen, showOutLine, /*sf, f,*/ filter);
};

CanvasRenderingContext2D.prototype.SimpleRoundRectangle = function (X, Y, Width, Height, borderRadius, fillStyle, strokeStyle)
{
    if (fillStyle)
        fillStyle.applySettings(this)
    if (strokeStyle)
        strokeStyle.applySettings(this)

    this.beginPath()

    this.roundRect(Math.round(X), Math.round(Y), Math.round(Width), Math.round(Height), borderRadius)  // borderRadius - a number or list specifying the radii of the circular arc to be used for the corners of the rectangle. The number and order of the radii function in the same way as the border-radius CSS property when width and height are positive

    this.closePath()

    if (fillStyle)
        this.fill()
    if (strokeStyle)
        this.stroke()
}

//#endregion

//#region Graphics primitives.

export let IGraphicsPrimitive = function () { };
IGraphicsPrimitive.prototype.applySettings = function (ctx) { };

//------------

export let LinearGradientBrush = function (x1, y1, x2, y2, colorStart, colorEnd)
{
    this.x1 = x1;
    this.y1 = y1;
    this.x2 = x2;
    this.y2 = y2;
    this.ColorStart = colorStart;
    this.ColorEnd = colorEnd;
};
LinearGradientBrush.prototype = Object.create(IGraphicsPrimitive.prototype);

LinearGradientBrush.prototype.applySettings = function (ctx)
{
    var grd = ctx.createLinearGradient(this.x1, this.y1, this.x2, this.y2);

    grd.addColorStop(0, this.ColorStart);
    grd.addColorStop(1, this.ColorEnd);

    ctx.fillStyle = grd;
};

//------------

export let SolidBrush = function (color)
{
    this.Color = color;
};
SolidBrush.prototype = Object.create(IGraphicsPrimitive.prototype);

SolidBrush.prototype.applySettings = function (ctx)
{
    ctx.fillStyle = this.Color;
};

//------------

export let Pen = function (color, width, dashStyle, lineCap)
{
    this.Color = color;
    this.Width = width || 1;
    this.DashStyle = dashStyle || [];
    this.LineCap = lineCap || '';
};
Pen.prototype = Object.create(IGraphicsPrimitive.prototype);

Pen.prototype.applySettings = function (ctx)
{
    ctx.strokeStyle = this.Color.Color || this.Color;
    ctx.lineWidth = this.Width;
    ctx.setLineDash(this.DashStyle);
    ctx.lineCap = this.LineCap;
};

Pen.prototype.copy = function ()
{
    return new Pen(
        this.Color,
        this.Width,
        this.DashStyle.slice());
};

//#region Process Pen

Pen.csSimpleChart = 0; // сплошная линия
Pen.csDotChart = 1; // точка (узловая)
Pen.csDotLineChart = 2; // точка на непрерывной линии
Pen.csIsoDotChart = 3; // точка (свободная)
Pen.csShapedChart = 4; // штриховая (линия-пробел-линия)
Pen.csShapedDotChart = 5; // штрих-пунктирная (линия-пробел-точка-пробел-линия)
Pen.csShapedDoubleDotChart = 6; // штрих-пунктирная (линия-пробел-точка-пробел-точка-пробел-линия)
Pen.csHistogrammChart = 7; // график - гистограмма
Pen.csArrowChart = 8; // график - стрелки
Pen.csHorisontalLineChart = 9; // график - горизонтальная линия в ширину бара
Pen.csLadder = 10; //ступеньки - соединяем горизонтальные линии в ширину бара

// Создать Pen с нужным стилем
Pen.ProcessPen = function (pen, chstyle)
{
    if (!pen) return null;

    switch (chstyle)
    {
        // line
        case Pen.csSimpleChart:
            //линия
            pen.DashStyle = [];
            break;
        // Dots
        case Pen.csDotChart:
            pen.DashStyle = [1, 1];
            break;
        // Dotted line
        case Pen.csDotLineChart:
            pen.DashStyle = [2, 2, 10, 2];
            break;
        // Iso dot
        case Pen.csIsoDotChart:
            pen.DashStyle = [2, 4];
            break;
        // Shaped line
        case Pen.csShapedChart:
            pen.DashStyle = [4, 2];
            break;
        // Dotted line
        case Pen.csShapedDotChart:
            pen.DashStyle = [2, 4, 7, 4];
            break;
        // Dash
        case Pen.csShapedDoubleDotChart:
            pen.DashStyle = [4, 5];
            break;
    }

    var width = pen.Width;
    var dashStyleArr = pen.DashStyle;
    var len = dashStyleArr.length;
    for (var i = 0; i < len; i++)
    {
        dashStyleArr[i] *= width;
    }
    return pen;
};

//#endregion

//------------

export let Font = function (height, family, fontWeight)
{
    this.Height = height;
    this.Family = family;
    this.FontWeight = fontWeight;
};
Font.prototype = Object.create(IGraphicsPrimitive.prototype);
Font.prototype.toString = function ()
{
    var str = this.Height + 'px ' + this.Family;

    if (this.FontWeight)
        str = this.FontWeight + ' ' + str;

    return str;
};

Font.prototype.copy = function ()
{
    return new Font(this.Height, this.Family, this.FontWeight);
};

Font.prototype.applySettings = function (ctx)
{
    ctx.font = this.toString();
};

Font.parseFontString = function (font)
{
    var splits = font.split(' ');
    let Font_name = ""
    for (let i = 1; i < splits.length; i++)
        Font_name += splits[i] + " ";
    return new Font(parseInt(splits[0]), Font_name.trim());
};


export let PolyLine = function ()
{
    this.lines = [];
}

export let PolyRect = function ()
{
    this.rects = [];
}

export let Line = function (x1, y1, x2, y2)
{
    this.X1 = x1;
    this.Y1 = y1;
    this.X2 = x2;
    this.Y2 = y2;
}

export let PolyText = function ()
{
    this.textArray = [];
}

export let PolyTextItem = function (text, x, y)
{
    this.Text = text;
    this.X = x;
    this.Y = y;
}
//#endregion

//------------

//#region Graphics

export let Graphics = {};

// For creating images etc.
Graphics.getAdditionalCanvas = function ()
{
    if (!Graphics.additionalCanvas)
        Graphics.additionalCanvas = document.createElement('canvas');

    return Graphics.additionalCanvas;
};

Graphics.getPixelColorFromImage = function (image, x, y)
{
    if (!image) return '';

    var canvas = Graphics.getAdditionalCanvas();
    canvas.width = image.width;
    canvas.height = image.height;

    var gr = canvas.getContext('2d');
    gr.drawImage(image, 0, 0);

    return gr.getPixelColor(x, y);
};

Graphics.makeGrayScale = function (src_image)
{
    if (!src_image) return null;

    var canvas = Graphics.getAdditionalCanvas();
    canvas.width = src_image.width;
    canvas.height = src_image.height;

    var gr = canvas.getContext('2d');
    gr.drawImage(src_image, 0, 0);

    var imageData = gr.getImageData(0, 0, src_image.width, src_image.height);
    var data = imageData.data;
    var len = data.length;
    for (var i = 0; i < len; i += 4)
    {
        var brightness = 0.34 * data[i] + 0.5 * data[i + 1] + 0.16 * data[i + 2];
        // red
        data[i] = brightness;
        // green
        data[i + 1] = brightness;
        // blue
        data[i + 2] = brightness;
    }
    // overwrite original image
    gr.putImageData(imageData, 0, 0);

    return gr.ToImage();
};

Graphics.createImageWithAlpha = function (image, alpha)
{
    if (!image) return null;

    var canvas = Graphics.getAdditionalCanvas();
    var gr = canvas.getContext('2d');

    var oldAlpha = gr.globalAlpha;

    canvas.width = image.width;
    canvas.height = image.height;

    gr.globalAlpha = alpha;

    gr.drawImage(image, 0, 0);

    var alphaImage = gr.ToImage();

    gr.globalAlpha = oldAlpha;

    return alphaImage;
};

//#region Contrast color

Graphics.GetContrastColor = function (color, twoColors = true)
{
    var channels = Color.getColorChannels(color);

    var r = channels.r;
    var g = channels.g;
    var b = channels.b;

    // Counting the perceptive luminance - human eye favors green color... 
    var luminance = 1 - (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    var coef = 0.33;

    if (luminance < 0.5)
    {
        //bright colors - black font
        if (twoColors) return 'rgb(0, 0, 0)';

        r = Graphics.GetContrastValue(r, 1 - coef);
        g = Graphics.GetContrastValue(g, 1 - coef);
        b = Graphics.GetContrastValue(b, 1 - coef);
    }
    else
    {
        // dark colors - white font
        if (twoColors) return 'rgb(255, 255, 255)';

        r = Graphics.GetContrastValue(r, 1 + coef);
        g = Graphics.GetContrastValue(g, 1 + coef);
        b = Graphics.GetContrastValue(b, 1 + coef);
    }

    return Color.CreateColor(Math.floor(r), Math.floor(g), Math.floor(b));
};

Graphics.GetContrastValue = function (i, coef)
{
    if (i < min) i = minValue;
    else if (i > max) i = maxValue;
    else i *= coef;

    return i;
};

//#endregion

//#endregion

//------------

//#region RectangleEdgeFilter

export let RectangleEdgeFilter = {
    None: 0,
    TopLeft: 1,
    TopRight: 2,
    BottomLeft: 4,
    BottomRight: 8
};
RectangleEdgeFilter.All =
    RectangleEdgeFilter.TopLeft |
    RectangleEdgeFilter.TopRight |
    RectangleEdgeFilter.BottomLeft |
    RectangleEdgeFilter.BottomRight;

RectangleEdgeFilter.IsTopLeft = function (filter)
{
    return (RectangleEdgeFilter.TopLeft & filter) === RectangleEdgeFilter.TopLeft;
};

RectangleEdgeFilter.IsTopRight = function (filter)
{
    return (RectangleEdgeFilter.TopRight & filter) === RectangleEdgeFilter.TopRight;
};

RectangleEdgeFilter.IsBottomRight = function (filter)
{
    return (RectangleEdgeFilter.BottomRight & filter) === RectangleEdgeFilter.BottomRight;
};

RectangleEdgeFilter.IsBottomLeft = function (filter)
{
    return (RectangleEdgeFilter.BottomLeft & filter) === RectangleEdgeFilter.BottomLeft;
};

//#endregion

//------------

//#region Color 

export let Color = function () { };

// alpha: 0 - 255. For compatibility with C#.
// colorString: rgb/rgba formats.
Color.FromArgb = function (alpha, colorString)
{
    var channels = Color.getColorChannels(colorString);

    var a = alpha / 255;

    return 'rgba(' +
        channels.r + ',' +
        channels.g + ',' +
        channels.b + ',' +
        a + ')';
};

Color.FromHexToArgb = function (alpha, colorString)
{
    let _colorString;
    if (Color.IsHexColor(colorString))
    {
        _colorString = Color.HexToRgb(colorString);
    } else
    {
        _colorString = Color.getColorChannels(colorString);;
    }

    var a = alpha / 255;

    return 'rgba(' +
        _colorString.r + ',' +
        _colorString.g + ',' +
        _colorString.b + ',' +
        a + ')';
};

Color.IsHexColor = function (hex)
{
    return /^#([0-9A-F]{3}|[0-9A-F]{4}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(hex)
}

Color.HexToRgb = function (hex)
{
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

// For rgb/rgba schemes only.
// minAlpha: 0 - 1.
Color.GetLighterColor = function (min, max, value, colorString, minAlpha)
{

    var overall = max - min;
    var correctionFactor = overall === 0 ? 1 : (value - min) / overall;

    var channels = Color.getColorChannels(colorString);
    var srcA = channels.hasOwnProperty('a') ? channels.a : 1;

    var alpha = srcA * correctionFactor;

    if (alpha > 1) alpha = 1;
    else if (alpha < minAlpha) alpha = minAlpha;

    return 'rgba(' +
        Math.round(channels.r) + ',' +
        Math.round(channels.g) + ',' +
        Math.round(channels.b) + ',' +
        alpha + ')';
};

// For rgb/rgba schemes only.
Color.getColorChannels = function (colorString)
{
    var channels = colorString.slice(colorString.indexOf('(') + 1).split(',');

    var result = {
        r: parseInt(channels[0]),
        g: parseInt(channels[1]),
        b: parseInt(channels[2])
    };

    if (channels.length > 3)
        result.a = parseFloat(channels[3]);

    return result;
};

// a - alpha (0 - 1).
Color.CreateColor = function (r, g, b, a)
{
    var color = (a === undefined || a === 1) ? 'rgb(' : 'rgba(';
    color += r + ',' + g + ',' + b;
    if (a !== undefined && a !== 1)
    {
        color += ',' + a;
    }
    color += ')';

    return color;
};


Color.Empty = null;
//Color.Gray = 'gray';
//Color.Violet = '#EE82EE';
//Color.Wheat = '#F5DEB3';
//Color.Red = '#FF0000';
Color.Transparent = '#FFFFFF';
Color.AliceBlue = '#F0F8FF';
Color.AntiqueWhite = '#FAEBD7';
Color.Aqua = '#00FFFF';
Color.Aquamarine = '#7FFFD4';
Color.Azure = '#F0FFFF';
Color.Beige = '#F5F5DC';
Color.Bisque = '#FFE4C4';
Color.Black = '#000000';
Color.BlanchedAlmond = '#FFEBCD';
Color.Blue = '#0000FF';
Color.BlueViolet = '#8A2BE2';
Color.Brown = '#A52A2A';
Color.BurlyWood = '#DEB887';
Color.CadetBlue = '#5F9EA0';
Color.Chartreuse = '#7FFF00';
Color.Chocolate = '#D2691E';
Color.Coral = '#FF7F50';
Color.CornflowerBlue = '#6495ED';
Color.Cornsilk = '#FFF8DC';
Color.Crimson = '#DC143C';
Color.Cyan = '#00FFFF';
Color.DarkBlue = '#00008B';
Color.DarkCyan = '#008B8B';
Color.DarkGoldenrod = '#B8860B';
Color.DarkGray = '#A9A9A9';
Color.DarkGreen = '#006400';
Color.DarkKhaki = '#BDB76B';
Color.DarkMagenta = '#8B008B';
Color.DarkOliveGreen = '#556B2F';
Color.DarkOrange = '#FF8C00';
Color.DarkOrchid = '#9932CC';
Color.DarkRed = '#8B0000';
Color.DarkSalmon = '#E9967A';
Color.DarkSeaGreen = '#8FBC8B';
Color.DarkSlateBlue = '#483D8B';
Color.DarkSlateGray = '#2F4F4F';
Color.DarkTurquoise = '#00CED1';
Color.DarkViolet = '#9400D3';
Color.DeepPink = '#FF1493';
Color.DeepSkyBlue = '#00BFFF';
Color.DimGray = '#696969';
Color.DodgerBlue = '#1E90FF';
Color.Firebrick = '#B22222';
Color.FloralWhite = '#FFFAF0';
Color.ForestGreen = '#228B22';
Color.Fuchsia = '#FF00FF';
Color.Gainsboro = '#DCDCDC';
Color.GhostWhite = '#F8F8FF';
Color.Gold = '#FFD700';
Color.Goldenrod = '#DAA520';
Color.Gray = '#808080';
Color.Green = '#008000';
Color.GreenYellow = '#ADFF2F';
Color.Honeydew = '#F0FFF0';
Color.HotPink = '#FF69B4';
Color.IndianRed = '#CD5C5C';
Color.Indigo = '#4B0082';
Color.Ivory = '#FFFFF0';
Color.Khaki = '#F0E68C';
Color.Lavender = '#E6E6FA';
Color.LavenderBlush = '#FFF0F5';
Color.LawnGreen = '#7CFC00';
Color.LemonChiffon = '#FFFACD';
Color.LightBlue = '#ADD8E6';
Color.LightCoral = '#F08080';
Color.LightCyan = '#E0FFFF';
Color.LightGoldenrodYellow = '#FAFAD2';
Color.LightGreen = '#90EE90';
Color.LightGray = '#D3D3D3';
Color.LightPink = '#FFB6C1';
Color.LightSalmon = '#FFA07A';
Color.LightSeaGreen = '#20B2AA';
Color.LightSkyBlue = '#87CEFA';
Color.LightSlateGray = '#778899';
Color.LightSteelBlue = '#B0C4DE';
Color.LightYellow = '#FFFFE0';
Color.Lime = '#00FF00';
Color.LimeGreen = '#32CD32';
Color.Linen = '#FAF0E6';
Color.Magenta = '#FF00FF';
Color.Maroon = '#800000';
Color.MediumAquamarine = '#66CDAA';
Color.MediumBlue = '#0000CD';
Color.MediumOrchid = '#BA55D3';
Color.MediumPurple = '#9370DB';
Color.MediumSeaGreen = '#3CB371';
Color.MediumSlateBlue = '#7B68EE';
Color.MediumSpringGreen = '#00FA9A';
Color.MediumTurquoise = '#48D1CC';
Color.MediumVioletRed = '#C71585';
Color.MidnightBlue = '#191970';
Color.MintCream = '#F5FFFA';
Color.MistyRose = '#FFE4E1';
Color.Moccasin = '#FFE4B5';
Color.NavajoWhite = '#FFDEAD';
Color.Navy = '#000080';
Color.OldLace = '#FDF5E6';
Color.Olive = '#808000';
Color.OliveDrab = '#6B8E23';
Color.Orange = '#FFA500';
Color.Orange2 = '#FF6600';
Color.OrangeRed = '#FF4500';
Color.Orchid = '#DA70D6';
Color.PaleGoldenrod = '#EEE8AA';
Color.PaleGreen = '#98FB98';
Color.PaleTurquoise = '#AFEEEE';
Color.PaleVioletRed = '#DB7093';
Color.PapayaWhip = '#FFEFD5';
Color.PeachPuff = '#FFDAB9';
Color.Peru = '#CD853F';
Color.Pink = '#FFC0CB';
Color.Plum = '#DDA0DD';
Color.PowderBlue = '#B0E0E6';
Color.Purple = '#800080';
Color.Red = '#FF0000';
Color.RosyBrown = '#BC8F8F';
Color.RoyalBlue = '#4169E1';
Color.SaddleBrown = '#8B4513';
Color.Salmon = '#FA8072';
Color.SandyBrown = '#F4A460';
Color.SeaGreen = '#2E8B57';
Color.SeaShell = '#FFF5EE';
Color.Sienna = '#A0522D';
Color.Silver = '#C0C0C0';
Color.SkyBlue = '#87CEEB';
Color.SlateBlue = '#6A5ACD';
Color.SlateGray = '#708090';
Color.Snow = '#FFFAFA';
Color.SpringGreen = '#00FF7F';
Color.SteelBlue = '#4682B4';
Color.Tan = '#D2B48C';
Color.Teal = '#008080';
Color.Thistle = '#D8BFD8';
Color.Tomato = '#FF6347';
Color.Turquoise = '#40E0D0';
Color.Violet = '#EE82EE';
Color.Wheat = '#F5DEB3';
Color.White = '#FFFFFF';
Color.WhiteSmoke = '#F5F5F5';
Color.Yellow = '#FFFF00';
Color.YellowGreen = '#9ACD32';

//#endregion

//#region Brushes
export let Brushes = function () { };

Brushes.White = new SolidBrush('white');
Brushes.Gray = new SolidBrush('gray');
Brushes.Red = new SolidBrush('red');
Brushes.Green = new SolidBrush('green');
Brushes.Black = new SolidBrush(Color.Black);

//#endregion

export let Pens = {};
Pens.White = new Pen('white');
Pens.Transparent = new Pen('transparent');
Pens.Black = new Pen(Color.Black);